import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

import { getBrowserWindow } from './lib/utils/browser-window';
import {
  API_KEY_PATTERN,
  AUTH_CALLBACK_PATH_PATTERN,
  BEARER_TOKEN_CHAR_PATTERN,
  buildLangfuseKeyPattern,
  HEX_CHAR_PATTERN,
  NAMESPACE_PATTERN,
  NEWLINE_PATTERN,
  POSTHOG_KEY_PATTERN,
  TURBOPUFFER_KEY_PATTERN,
  TWITTER_ID_PATTERN,
} from './lib/utils/regex.ts';
import { isRecord } from './lib/utils/type-guards';

const isReactNativeRuntime =
  navigator !== undefined && typeof navigator.userAgent === 'string' && navigator.userAgent.includes('ReactNative');

// =============================================================
// ADVANCED VALIDATION PATTERNS
// =============================================================

/** Custom validator for environment variables that should not contain newlines */
const noNewlines = <T extends z.ZodType<string | undefined>>(schema: T): T =>
  schema.refine((value: string | undefined) => !(value && NEWLINE_PATTERN.test(value)), {
    message: 'Environment variable must not contain newlines',
  });

/** Validate that a string looks like a valid API key (alphanumeric + special chars, min length) */
const apiKey = (minLength = 20) =>
  z
    .string()
    .min(minLength, `API key must be at least ${minLength} characters`)
    .regex(API_KEY_PATTERN, 'API key contains invalid characters');

/** Validate email addresses with better error messages */
const email = z.email('Must be a valid email address');

/** Validate that a string contains comma-separated Twitter/X user IDs */
const twitterIdList = z
  .string()
  .refine(
    (value) => value.split(',').every((id) => TWITTER_ID_PATTERN.test(id.trim())),
    'Must be comma-separated numeric Twitter user IDs'
  );

/** Validate Stripe keys with proper prefixes */
const stripeKey = (prefix: string) =>
  z
    .string()
    .startsWith(prefix, `Stripe key must start with "${prefix}"`)
    .min(prefix.length + 10, 'Stripe key appears to be too short');

/** Validate encryption keys (must be exactly 64 hex characters for AES-256) */
const encryptionKey = z
  .string()
  .length(64, 'Encryption key must be exactly 64 hex characters (32 bytes) for AES-256')
  .regex(HEX_CHAR_PATTERN, 'Encryption key must be valid hexadecimal');

const normalizeBooleanFlag = (value: string | number | boolean): string => {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return value === 0 ? 'false' : 'true';
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return trimmed;
  }
  const normalized = trimmed.toLowerCase();
  if (['true', '1', 'yes', 'on', 'enabled'].includes(normalized)) {
    return 'true';
  }
  if (['false', '0', 'no', 'off', 'disabled'].includes(normalized)) {
    return 'false';
  }
  return normalized;
};

const PUBLIC_ENV_KEYS = [
  'NEXT_PUBLIC_APP_URL',
  'NEXT_PUBLIC_BUILD_MODE',
  'NEXT_PUBLIC_POSTHOG_KEY',
  'NEXT_PUBLIC_POSTHOG_HOST',
  'NEXT_PUBLIC_VAPID_PUBLIC_KEY',
  'NEXT_PUBLIC_MSW',
  'NEXT_PUBLIC_API_MOCK',
  'NEXT_PUBLIC_TWITTER_SNAPSHOTS',
  'NEXT_PUBLIC_TWITTER_DATA_SOURCE',
  'NEXT_PUBLIC_PLAYWRIGHT_TEST',
  'NEXT_PUBLIC_ENABLE_RT_DEBUG',
  'NEXT_PUBLIC_TRPC_BATCH_DEV',
  'NEXT_PUBLIC_TWEET_MEDIA_DEBUG',
  'NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN',
  'NEXT_PUBLIC_BETTER_STACK_INGESTING_URL',
  'NEXT_PUBLIC_VERCEL_ENV',
  'NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN',
] as const satisfies readonly (keyof EnvironmentInstance)[];

type PublicEnvKey = (typeof PUBLIC_ENV_KEYS)[number];

const BOOLEAN_ENV_KEY_NAMES = [
  'INNGEST_DEV',
  'DISABLE_PROFILES_FETCH_MISSING',
  'ENABLE_TWEET_MEDIA_LINK_DEDUPE',
  'EXTERNAL_API_TRACKER_STORE_RESPONSES',
  'DEBUG_SQL',
  'DEBUG_TIMELINE_CANCEL',
  'VITEST_DEBUG_TRPC_MUTATION',
  'ENABLE_INTEGRATION_DB',
  'TWITTER_SNAPSHOTS',
  'PLAYWRIGHT_TEST',
  'PLAYWRIGHT_PULSE_ONLINE',
  'VERCEL',
  'NEXT_PUBLIC_MSW',
  'NEXT_PUBLIC_API_MOCK',
  'NEXT_PUBLIC_TWITTER_SNAPSHOTS',
  'NEXT_PUBLIC_PLAYWRIGHT_TEST',
  'NEXT_PUBLIC_ENABLE_RT_DEBUG',
  'NEXT_PUBLIC_TRPC_BATCH_DEV',
  'NEXT_PUBLIC_TWEET_MEDIA_DEBUG',
] as const;

const BOOLEAN_ENV_KEY_SET = new Set<string>(BOOLEAN_ENV_KEY_NAMES);

const PUBLIC_ENV_KEY_SET = new Set<string>(PUBLIC_ENV_KEYS);

const isPublicEnvKey = (key: string): key is PublicEnvKey => PUBLIC_ENV_KEY_SET.has(key);

type NextPublicProcessEnv = NodeJS.ProcessEnv & {
  NEXT_PUBLIC_APP_URL?: string;
  NEXT_PUBLIC_BUILD_MODE?: string;
  NEXT_PUBLIC_POSTHOG_KEY?: string;
  NEXT_PUBLIC_POSTHOG_HOST?: string;
  NEXT_PUBLIC_VAPID_PUBLIC_KEY?: string;
  NEXT_PUBLIC_MSW?: string;
  NEXT_PUBLIC_API_MOCK?: string;
  NEXT_PUBLIC_TWITTER_SNAPSHOTS?: string;
  NEXT_PUBLIC_TWITTER_DATA_SOURCE?: string;
  NEXT_PUBLIC_PLAYWRIGHT_TEST?: string;
  NEXT_PUBLIC_ENABLE_RT_DEBUG?: string;
  NEXT_PUBLIC_TRPC_BATCH_DEV?: string;
  NEXT_PUBLIC_TWEET_MEDIA_DEBUG?: string;
  NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN?: string;
  NEXT_PUBLIC_BETTER_STACK_INGESTING_URL?: string;
  NEXT_PUBLIC_VERCEL_ENV?: string;
  NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN?: string;
};

const nextPublicProcessEnv = process.env as NextPublicProcessEnv;

const runtimePublicEnvValues: Record<PublicEnvKey, string | undefined> = {
  // NOTE: Use direct access so Next.js can inline NEXT_PUBLIC_* values into client bundles.
  NEXT_PUBLIC_APP_URL: nextPublicProcessEnv.NEXT_PUBLIC_APP_URL,
  NEXT_PUBLIC_BUILD_MODE: nextPublicProcessEnv.NEXT_PUBLIC_BUILD_MODE,
  NEXT_PUBLIC_POSTHOG_KEY: nextPublicProcessEnv.NEXT_PUBLIC_POSTHOG_KEY,
  NEXT_PUBLIC_POSTHOG_HOST: nextPublicProcessEnv.NEXT_PUBLIC_POSTHOG_HOST,
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: nextPublicProcessEnv.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
  NEXT_PUBLIC_MSW: nextPublicProcessEnv.NEXT_PUBLIC_MSW,
  NEXT_PUBLIC_API_MOCK: nextPublicProcessEnv.NEXT_PUBLIC_API_MOCK,
  NEXT_PUBLIC_TWITTER_SNAPSHOTS: nextPublicProcessEnv.NEXT_PUBLIC_TWITTER_SNAPSHOTS,
  NEXT_PUBLIC_TWITTER_DATA_SOURCE: nextPublicProcessEnv.NEXT_PUBLIC_TWITTER_DATA_SOURCE,
  NEXT_PUBLIC_PLAYWRIGHT_TEST: nextPublicProcessEnv.NEXT_PUBLIC_PLAYWRIGHT_TEST,
  NEXT_PUBLIC_ENABLE_RT_DEBUG: nextPublicProcessEnv.NEXT_PUBLIC_ENABLE_RT_DEBUG,
  NEXT_PUBLIC_TRPC_BATCH_DEV: nextPublicProcessEnv.NEXT_PUBLIC_TRPC_BATCH_DEV,
  NEXT_PUBLIC_TWEET_MEDIA_DEBUG: nextPublicProcessEnv.NEXT_PUBLIC_TWEET_MEDIA_DEBUG,
  NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN: nextPublicProcessEnv.NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN,
  NEXT_PUBLIC_BETTER_STACK_INGESTING_URL: nextPublicProcessEnv.NEXT_PUBLIC_BETTER_STACK_INGESTING_URL,
  NEXT_PUBLIC_VERCEL_ENV: nextPublicProcessEnv.NEXT_PUBLIC_VERCEL_ENV,
  NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN: nextPublicProcessEnv.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
};

const runtimeEnvValue = (key: string): string | undefined => {
  if (isPublicEnvKey(key)) {
    return runtimePublicEnvValues[key];
  }

  if (process?.env) {
    const value = process.env[key];
    if (value !== undefined) {
      return value;
    }
  }

  return;
};

const booleanFlag = z
  .union([z.string(), z.boolean(), z.number()])
  .transform<string>((value) => normalizeBooleanFlag(value))
  .pipe(z.enum(['true', 'false']))
  .describe('Boolean flag must be "true"/"false", "1"/"0", "yes"/"no", or "on"/"off"');

/**
 * We no longer gate required variables by NODE_ENV.
 * Variables that the app depends on are required in all environments.
 */

/** Enhanced bearer token validation */
const bearerToken = z
  .string()
  .min(50, 'Bearer token appears to be too short')
  .regex(BEARER_TOKEN_CHAR_PATTERN, 'Bearer token contains invalid characters');

/** Validate Turbopuffer API keys with proper tpuf_ prefix */
const turbopufferKey = z
  .string()
  .startsWith('tpuf_', 'Turbopuffer API key must start with "tpuf_"')
  .min(35, 'Turbopuffer API key appears to be too short')
  .regex(TURBOPUFFER_KEY_PATTERN, 'Turbopuffer API key must be alphanumeric after the prefix');

const buildLangfuseKeySchema = (prefix: 'sk-lf-' | 'pk-lf-') =>
  z
    .string()
    .min(prefix.length + 16, `Langfuse key must be at least ${prefix.length + 16} characters`)
    .startsWith(prefix, `Langfuse key must start with "${prefix}"`)
    .regex(buildLangfuseKeyPattern(prefix), 'Langfuse key may contain only letters, numbers, and hyphens');

const environmentInstance = createEnv({
  // =============================================================
  // SERVER-SIDE ENVIRONMENT VARIABLES
  // =============================================================
  server: {
    // --- Database ---
    DATABASE_URL: noNewlines(
      z.url('DATABASE_URL must be a valid PostgreSQL connection URL').describe('Primary Postgres connection string')
    ),
    // Optional: used by local scripts, Atlas, and tests; not guaranteed in prod
    DIRECT_DATABASE_URL: noNewlines(z.url('DIRECT_DATABASE_URL must be a valid PostgreSQL connection URL').optional()),
    SWEETISTICS_SCHEMA_PATH: noNewlines(
      z.string().optional().describe('Optional override path for the desktop PGlite schema file')
    ),
    ENFORCE_DB_TRANSACTIONS: z
      .enum(['true', 'false'])
      .default('false')
      .describe('When set to "true", integration tests must wrap DB writes in transactions'),

    // --- Authentication ---
    AUTH_SECRET: noNewlines(
      z
        .string()
        .min(32, 'AUTH_SECRET must be at least 32 characters for security')
        .describe('Better Auth encryption/signing secret')
    ),
    AUTH_URL: noNewlines(z.url('AUTH_URL must be a valid URL').optional()),
    PRODUCTION_APP_URL: noNewlines(
      z.url('PRODUCTION_APP_URL must be a valid URL').optional().describe('Canonical app domain for emails and links')
    ),

    // --- Twitter/X API ---
    TWITTER_CLIENT_ID: noNewlines(z.string().min(1, 'TWITTER_CLIENT_ID is required')),
    TWITTER_CLIENT_SECRET: noNewlines(apiKey(30).describe('Twitter OAuth client secret')),
    TWITTER_API_KEY: noNewlines(apiKey().optional().describe('Twitter API v1.1 key')),
    TWITTER_API_SECRET: noNewlines(apiKey().optional().describe('Twitter API v1.1 secret')),
    TWITTER_BEARER_TOKEN: noNewlines(bearerToken.describe('Twitter API v2 bearer token')),
    TWITTER_DATA_SOURCE: z
      .enum(['twitter', 'graphql', 'hybrid'])
      .default('twitter')
      .describe('Primary data source for Twitter timelines (twitter, graphql, or hybrid)'),
    X_ACTIVITY_CONSUMER_SECRET: noNewlines(
      apiKey(30)
        .optional()
        .describe('Override for X Activity webhook consumer secret; falls back to TWITTER_CLIENT_SECRET when unset')
    ),
    X_ACTIVITY_ENV: z
      .enum(['dev', 'prod', 'staging'])
      .default('dev')
      .describe('Environment tag used when verifying X Activity webhooks'),

    // --- AI & Embeddings ---
    OPENAI_API_KEY: noNewlines(apiKey().describe('OpenAI API key for GPT models')),
    AI_GATEWAY_API_KEY: noNewlines(apiKey().describe('Vercel AI Gateway API key')),
    XAI_API_KEY: noNewlines(apiKey().optional().describe('xAI API key for Grok models')),
    GEMINI_API_KEY: noNewlines(apiKey().optional().describe('Google Gemini API key')),
    AI_USAGE_SYSTEM_USER_ID: noNewlines(
      z
        .uuid({ message: 'AI_USAGE_SYSTEM_USER_ID must be a valid UUID' })
        .optional()
        .describe('User ID used to attribute system AI usage entries')
    ),
    TURBOPUFFER_API_KEY: noNewlines(turbopufferKey.describe('Turbopuffer vector database API key')),
    TURBOPUFFER_NAMESPACE: noNewlines(
      z
        .string()
        .min(1)
        .regex(NAMESPACE_PATTERN, 'Namespace must be alphanumeric with underscores/hyphens only')
        .optional()
        .describe('Turbopuffer namespace for data isolation')
    ),
    SIGNOZ_MODE: z
      .enum(['cloud', 'local'])
      .default('cloud')
      .describe('Select SigNoz deployment mode (cloud or local docker fallback)'),
    SIGNOZ_URL: noNewlines(
      z
        .url('SIGNOZ_URL must be a valid URL')
        .optional()
        .describe('SigNoz Cloud tenant base URL (e.g. https://able-corgi.us.signoz.cloud)')
    ),
    SIGNOZ_TOKEN: noNewlines(
      z
        .string()
        .min(20, 'SIGNOZ_TOKEN appears too short')
        .optional()
        .describe('SigNoz API key / PAT used by MCP + bootstrap helpers')
    ),
    SIGNOZ_OTLP_ENDPOINT: noNewlines(
      z
        .url('SIGNOZ_OTLP_ENDPOINT must be a valid URL')
        .optional()
        .describe('Optional override for OTLP endpoint when not using OTEL_EXPORTER_OTLP_ENDPOINT')
    ),
    TURBOPUFFER_REGION: z
      .enum([
        'gcp-europe-west3',
        'gcp-us-east4',
        'gcp-us-west1',
        'gcp-asia-southeast1',
        'gcp-us-central1',
        'gcp-northamerica-northeast2',
        'aws-eu-central-1',
        'aws-us-east-1',
        'aws-us-east-2',
        'aws-us-west-2',
        'aws-ap-southeast-2',
      ])
      .optional()
      .describe('Turbopuffer region'),

    // --- Background Jobs (Inngest) ---
    INNGEST_EVENT_KEY: noNewlines(apiKey(10).describe('Inngest event API key')),
    INNGEST_SIGNING_KEY: noNewlines(apiKey().describe('Inngest webhook signing key')),
    INNGEST_DEV: noNewlines(
      z
        .enum(['0', '1'])
        .optional()
        .describe('Set to "1" when the Inngest dev server is active so diagnostics assume local mode')
    ),
    INNGEST_DEV_URL: noNewlines(z.url('Inngest development server URL').optional()).describe(
      'Overrides the Inngest HTTPS endpoint when running the local dev server'
    ),
    INNGEST_BASE_URL: noNewlines(z.url('Inngest base URL for events').optional()).describe(
      'Explicit Inngest base URL for manual event routing'
    ),
    DISABLE_PROFILES_FETCH_MISSING: z
      .enum(['true', 'false'])
      .default('true')
      .describe('Leave at "true" (default) to short-circuit profiles.fetch-missing; set "false" to allow fetches'),
    ENABLE_TWEET_MEDIA_LINK_DEDUPE: z
      .enum(['0', '1'])
      .default('1')
      .describe('Set to "1" (default) to run tweet media/link dedupe; switch to "0" to disable the pipeline'),

    // --- External Services ---
    STRIPE_SECRET_KEY: stripeKey('sk_').describe('Stripe secret key for payments'),
    RESEND_API_KEY: stripeKey('re_').describe('Resend API key for transactional emails'),
    APIFY_API_TOKEN: noNewlines(apiKey().optional().describe('Apify web scraping API token')),
    FIRECRAWL_API_KEY: noNewlines(apiKey().optional().describe('Firecrawl scrape API key for markdown extraction')),
    FIRECRAW_API_KEY: noNewlines(
      apiKey()
        .optional()
        .describe('Deprecated Firecrawl API key (fallback until environment is renamed to FIRECRAWL_API_KEY)')
    ),

    // --- Geocoding ---
    OPENCAGE_API_KEY: noNewlines(apiKey().describe('OpenCage Geocoding API key')),
    MAPBOX_ACCESS_TOKEN: noNewlines(
      z.string().optional().describe('Mapbox access token (server-side fallback for Explore map + geocoding)')
    ),
    VERCEL_KV_REST_API_URL: noNewlines(z.url('VERCEL_KV_REST_API_URL must be a valid URL').optional()).describe(
      'Base URL for the Upstash/Vercel Redis REST client'
    ),
    VERCEL_KV_REST_API_TOKEN: noNewlines(z.string().min(1).optional()).describe(
      'Writable Upstash/Vercel Redis REST API token'
    ),
    VERCEL_KV_REST_API_READ_ONLY_TOKEN: noNewlines(z.string().min(1).optional()).describe(
      'Read-only Upstash/Vercel Redis REST API token'
    ),
    SWEETISTICS_OTEL_IMPORT: noNewlines(
      z.string().optional().describe('Optional module path to initialize OpenTelemetry logging')
    ),
    OTEL_EXPORTER_OTLP_ENDPOINT: noNewlines(
      z.string().optional().describe('OpenTelemetry OTLP HTTP endpoint for log export')
    ),
    OTEL_SERVICE_NAME: noNewlines(z.string().min(1).optional()).describe(
      'Override the OpenTelemetry service name reported to collectors'
    ),
    OTEL_DIAGNOSTICS: noNewlines(z.string().optional()).describe('Set to "debug" to enable OTEL diagnostics logs'),

    // --- Web Push Notifications (VAPID) ---
    VAPID_PUBLIC_KEY: noNewlines(
      z
        .string()
        .length(87, 'VAPID public key must be 87 characters (base64url-encoded)')
        .optional()
        .describe('VAPID public key for web push notifications')
    ),
    VAPID_PRIVATE_KEY: noNewlines(
      z
        .string()
        .length(43, 'VAPID private key must be 43 characters (base64url-encoded)')
        .optional()
        .describe('VAPID private key for web push notifications')
    ),
    VAPID_EMAIL: noNewlines(email.optional().describe('VAPID contact email for push notifications')),

    // --- Security & Secrets ---
    ENCRYPTION_KEY: noNewlines(encryptionKey.describe('AES-256 encryption key for sensitive data')),
    CRON_SECRET: noNewlines(z.string().min(16, 'CRON_SECRET should be at least 16 characters for security')).describe(
      'Shared bearer token used by the cron health check endpoint'
    ),
    CLI_SECRET: noNewlines(
      z.string().min(16, 'CLI_SECRET should be at least 16 characters for security').optional()
    ).describe('Optional authentication secret for Sweetistics CLI commands'),

    // --- Admin & Development ---
    ADMIN_TWITTER_IDS: noNewlines(twitterIdList.optional().describe('Comma-separated list of admin Twitter user IDs')),
    DEBUG_SQL: z
      .enum(['true', '1', 'yes'])
      .optional()
      .describe('Enable verbose SQL logging in development (pretty printer + query timing)'),
    DEBUG_TIMELINE_CANCEL: z
      .enum(['0', '1'])
      .optional()
      .describe('Enable timeline cancel instrumentation and global debug snapshot'),
    VITEST_DEBUG_TRPC_MUTATION: z
      .enum(['true', 'false', '1', '0'])
      .optional()
      .describe('Enable verbose tRPC mutation debug logging during Vitest runs (experimental tooling hook)'),
    DEV_ENV_MODE: z
      .enum(['dev-local', 'prod-like', 'local', 'preview', 'production', 'staging'])
      .optional()
      .describe('Development environment mode'),
    DEV_ENV_LABEL: noNewlines(z.string().max(20, 'Environment label should be concise').optional()),
    ENABLE_INTEGRATION_DB: booleanFlag
      .optional()
      .describe('Use the live Postgres database during tests instead of the in-memory mock'),
    SWEETISTICS_PROFILE_RESOLUTION_DISABLED: booleanFlag
      .optional()
      .describe('Disable enqueueing missing-profile resolution requests (emergency kill switch)'),
    TWITTER_SNAPSHOTS: z
      .enum(['0', '1'])
      .optional()
      .describe('Force offline Twitter snapshot mode server-side (treats "1" as recorded data only)'),
    PLAYWRIGHT_TEST: booleanFlag
      .optional()
      .describe('Marks Playwright automation runs (enables mock auth, offline fixtures, and test shortcuts)'),
    PLAYWRIGHT_RUNTIME: booleanFlag
      .optional()
      .describe('Legacy Playwright runtime flag for compatibility with older automation entry points'),
    PLAYWRIGHT_TEST_RUNTIME: booleanFlag
      .optional()
      .describe('Legacy Playwright runtime flag for compatibility with older automation entry points'),
    PLAYWRIGHT_MOCK_AUTH: booleanFlag
      .optional()
      .describe('Force mock auth on server layouts/routes without enabling the full automation fixture suite'),
    PLAYWRIGHT_AUTH_DEBUG: booleanFlag
      .optional()
      .describe('Emit server-side auth bypass diagnostics during Playwright automation'),
    PLAYWRIGHT_PULSE_ONLINE: z
      .enum(['true', 'false', '1', '0'])
      .optional()
      .describe('Force Pulse queries to hit live data in Playwright (also lowers significance thresholds)'),
    PLAYWRIGHT_AUTH_TIMESTAMP: noNewlines(
      z.iso.datetime({ offset: true }).optional().describe('ISO timestamp for mock auth sessions in Playwright flows')
    ),
    SWEETISTICS_PGLITE_MODE: z
      .enum(['memory', 'persistent'])
      .optional()
      .describe('Desktop PGlite storage mode (memory or persistent)'),

    // --- Platform Detection ---
    VERCEL: noNewlines(
      z
        .string()
        .optional()
        .describe('Set to "1" on Vercel deployments (used to disable pretty logging and tag platform runs)')
    ),
    VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional().describe('Vercel environment'),
    VERCEL_REGION: noNewlines(
      z.string().optional().describe('Vercel region code for the current function (e.g., iad1)')
    ),
    VERCEL_GIT_COMMIT_SHA: noNewlines(
      z.string().optional().describe('Vercel git commit SHA for the current deployment')
    ),
    VERCEL_DEPLOYMENT_ID: noNewlines(z.string().optional().describe('Vercel deployment identifier')),
    NEXT_RUNTIME: z.enum(['nodejs', 'edge']).optional().describe('Next.js runtime environment'),
    HOSTNAME: noNewlines(z.string().optional().describe('Runtime hostname (platform provided)')),
    AWS_LAMBDA_FUNCTION_NAME: noNewlines(z.string().optional().describe('AWS Lambda function name')),

    // --- Node Environment ---
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development').describe('Node.js environment mode'),

    // --- Logging & Monitoring ---
    SWEETISTICS_LOG_LEVEL: z
      .enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent'])
      .optional()
      .describe('Override the default server log level (trace, debug, info, warn, error, fatal, silent)'),
    LANGFUSE_SECRET_KEY: noNewlines(
      buildLangfuseKeySchema('sk-lf-').describe('Langfuse secret API key for server-side tracing')
    ),
    LANGFUSE_PUBLIC_KEY: noNewlines(
      buildLangfuseKeySchema('pk-lf-').describe('Langfuse public API key for trace ingestion')
    ),
    LANGFUSE_HOST: noNewlines(z.url('Langfuse host must be a valid URL').describe('Langfuse instance base URL')),
    // --- Observability ---
    SENTRY_DSN: noNewlines(z.url('SENTRY_DSN must be a valid URL').optional()),

    // Better Stack admin observability
    BETTERSTACK_API_TOKEN: noNewlines(
      apiKey().optional().describe('Better Stack Telemetry API token used to resolve log sources')
    ),
    BETTERSTACK_QUERY_ENDPOINT: noNewlines(
      z
        .url('Better Stack query endpoint must be a valid URL')
        .optional()
        .describe('Better Stack Remote Connect endpoint (defaults to eu-nbg-2 region)')
    ),
    BETTERSTACK_QUERY_USERNAME: noNewlines(
      z.string().min(1).optional().describe('Better Stack Remote Connect username')
    ),
    BETTERSTACK_QUERY_PASSWORD: noNewlines(
      z.string().min(1).optional().describe('Better Stack Remote Connect password')
    ),
    TWITTER_GRAPHQL_AUTH_TOKEN: noNewlines(z.string().min(1).optional()).describe(
      'Auth token used for Twitter GraphQL requests'
    ),
    TWITTER_GRAPHQL_CT0: noNewlines(z.string().min(1).optional()).describe(
      'ct0 cookie used for Twitter GraphQL requests'
    ),
    AUTH_TOKEN: noNewlines(z.string().min(1).optional()).describe('Twitter REST API auth token override'),
    CT0: noNewlines(z.string().min(1).optional()).describe('Twitter REST API ct0 cookie override'),
    BETTERSTACK_DEFAULT_SOURCE: noNewlines(
      z.string().min(1).optional().describe('Default Better Stack source name to query for logs')
    ),
    EXTERNAL_API_TRACKER_STORE_RESPONSES: booleanFlag
      .default('false')
      .transform((value) => value === 'true')
      .describe('Enable storing raw external API responses in externalApiCalls'),
    EXPO_PUBLIC_APP_SCHEME: noNewlines(
      z.string().trim().min(1).optional().describe('Expo deep-link scheme override for the mobile app')
    ),
    EXPO_PUBLIC_API_URL: noNewlines(
      z.url('EXPO_PUBLIC_API_URL must be a valid URL').optional().describe('Override API base URL for Expo bundles')
    ),
    EXPO_PUBLIC_WEB_URL: noNewlines(
      z.url('EXPO_PUBLIC_WEB_URL must be a valid URL').optional().describe('Override web URL surfaced to Expo bundles')
    ),
    EXPO_PUBLIC_AUTH_CALLBACK_PATH: noNewlines(
      z
        .string()
        .trim()
        .regex(AUTH_CALLBACK_PATH_PATTERN, 'Auth callback path must start with "/"')
        .optional()
        .describe('Expo auth callback path (relative to the configured web URL)')
    ),
  },

  // =============================================================
  // CLIENT-SIDE ENVIRONMENT VARIABLES
  // =============================================================
  client: {
    NEXT_PUBLIC_APP_URL: noNewlines(
      z
        .url('Application base URL must be valid')
        .describe('Base URL for the application, used for OAuth redirects and API calls')
    ),
    NEXT_PUBLIC_BUILD_MODE: z
      .enum(['cloud', 'desktop'])
      .default('cloud')
      .describe('Controls whether the app runs in cloud (Next.js) or desktop (Tauri) mode'),

    NEXT_PUBLIC_POSTHOG_KEY: noNewlines(
      z
        .string()
        .regex(POSTHOG_KEY_PATTERN, 'PostHog key must start with "phc_" followed by at least 40 characters')
        .describe('PostHog analytics project key')
    ),

    NEXT_PUBLIC_POSTHOG_HOST: noNewlines(
      z.url('PostHog host must be a valid URL').describe('PostHog analytics host URL')
    ),

    NEXT_PUBLIC_VAPID_PUBLIC_KEY: noNewlines(
      z
        .string()
        .length(87, 'VAPID public key must be 87 characters (base64url-encoded)')
        .optional()
        .describe('VAPID public key for web push notifications (client-side)')
    ),

    // --- Development & Testing ---
    NEXT_PUBLIC_MSW: z.enum(['true', '1', 'enabled']).optional().describe('Enable Mock Service Worker for API mocking'),

    NEXT_PUBLIC_API_MOCK: z
      .enum(['true', '1', 'enabled'])
      .optional()
      .describe('Enable MSW-based API mocking and mock auth flow in automated/browser builds'),

    NEXT_PUBLIC_TWITTER_SNAPSHOTS: z
      .enum(['true', 'false', '1', '0'])
      .optional()
      .describe('Force offline Twitter snapshot mode in the browser (treat recorded fixtures as the source of truth)'),
    NEXT_PUBLIC_TWITTER_DATA_SOURCE: z
      .enum(['twitter', 'graphql', 'hybrid'])
      .default('twitter')
      .describe('Selected Twitter data source variant exposed to the client'),
    NEXT_PUBLIC_PLAYWRIGHT_TEST: z
      .enum(['true', 'false', '1', '0'])
      .optional()
      .describe('Marks Playwright automation bundles (enables mock timeline payloads and testing overlays)'),
    NEXT_PUBLIC_ENABLE_RT_DEBUG: z
      .enum(['true', 'false', '1', '0'])
      .optional()
      .describe('Enable realtime debugging helpers in client bundles (prod/preview opt-in; dev auto-enables)'),
    NEXT_PUBLIC_TRPC_BATCH_DEV: booleanFlag
      .optional()
      .describe('Enable tRPC batch stream transport in development to match production batching'),

    // --- Logging & Monitoring ---
    NEXT_PUBLIC_TWEET_MEDIA_DEBUG: z
      .enum(['true', 'false', '1', '0'])
      .optional()
      .describe('Enable verbose tweet media logging in client bundles'),

    NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN: noNewlines(apiKey().describe('Better Stack source token (client/server)')),
    NEXT_PUBLIC_BETTER_STACK_INGESTING_URL: noNewlines(
      z.url('Better Stack ingesting URL must be a valid URL').describe('Better Stack ingesting endpoint URL')
    ),

    // --- Maps ---
    NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN: noNewlines(
      z
        .string()
        .min(1, 'Mapbox access token is required to render the map')
        .optional()
        .describe('Mapbox GL JS access token for Explore Map')
    ),

    // --- Platform Detection ---
    NEXT_PUBLIC_VERCEL_ENV: z
      .enum(['production', 'preview', 'development'])
      .optional()
      .describe('Vercel environment detection'),
  },

  // =============================================================
  // RUNTIME-ACCESSIBLE ENVIRONMENT VARIABLES
  // =============================================================
  runtimeEnv: {
    // Server
    DATABASE_URL: runtimeEnvValue('DATABASE_URL'),
    DIRECT_DATABASE_URL: runtimeEnvValue('DIRECT_DATABASE_URL'),
    AUTH_SECRET: runtimeEnvValue('AUTH_SECRET'),
    AUTH_URL: runtimeEnvValue('AUTH_URL'),
    PRODUCTION_APP_URL: runtimeEnvValue('PRODUCTION_APP_URL'),
    TWITTER_CLIENT_ID: runtimeEnvValue('TWITTER_CLIENT_ID'),
    TWITTER_CLIENT_SECRET: runtimeEnvValue('TWITTER_CLIENT_SECRET'),
    TWITTER_API_KEY: runtimeEnvValue('TWITTER_API_KEY'),
    TWITTER_API_SECRET: runtimeEnvValue('TWITTER_API_SECRET'),
    TWITTER_BEARER_TOKEN: runtimeEnvValue('TWITTER_BEARER_TOKEN'),
    TWITTER_DATA_SOURCE: runtimeEnvValue('TWITTER_DATA_SOURCE'),
    X_ACTIVITY_CONSUMER_SECRET: runtimeEnvValue('X_ACTIVITY_CONSUMER_SECRET'),
    X_ACTIVITY_ENV: runtimeEnvValue('X_ACTIVITY_ENV'),
    OPENAI_API_KEY: runtimeEnvValue('OPENAI_API_KEY'),
    AI_GATEWAY_API_KEY: runtimeEnvValue('AI_GATEWAY_API_KEY'),
    XAI_API_KEY: runtimeEnvValue('XAI_API_KEY'),
    GEMINI_API_KEY: runtimeEnvValue('GEMINI_API_KEY'),
    AI_USAGE_SYSTEM_USER_ID: runtimeEnvValue('AI_USAGE_SYSTEM_USER_ID'),
    TURBOPUFFER_API_KEY: runtimeEnvValue('TURBOPUFFER_API_KEY'),
    TURBOPUFFER_NAMESPACE: runtimeEnvValue('TURBOPUFFER_NAMESPACE'),
    TURBOPUFFER_REGION: runtimeEnvValue('TURBOPUFFER_REGION'),
    INNGEST_EVENT_KEY: runtimeEnvValue('INNGEST_EVENT_KEY'),
    INNGEST_SIGNING_KEY: runtimeEnvValue('INNGEST_SIGNING_KEY'),
    INNGEST_DEV: runtimeEnvValue('INNGEST_DEV'),
    INNGEST_DEV_URL: runtimeEnvValue('INNGEST_DEV_URL'),
    INNGEST_BASE_URL: runtimeEnvValue('INNGEST_BASE_URL'),
    SWEETISTICS_OTEL_IMPORT: runtimeEnvValue('SWEETISTICS_OTEL_IMPORT'),
    OTEL_EXPORTER_OTLP_ENDPOINT: runtimeEnvValue('OTEL_EXPORTER_OTLP_ENDPOINT'),
    OTEL_SERVICE_NAME: runtimeEnvValue('OTEL_SERVICE_NAME'),
    OTEL_DIAGNOSTICS: runtimeEnvValue('OTEL_DIAGNOSTICS'),
    SIGNOZ_MODE: runtimeEnvValue('SIGNOZ_MODE'),
    SIGNOZ_URL: runtimeEnvValue('SIGNOZ_URL'),
    SIGNOZ_TOKEN: runtimeEnvValue('SIGNOZ_TOKEN'),
    SIGNOZ_OTLP_ENDPOINT: runtimeEnvValue('SIGNOZ_OTLP_ENDPOINT'),
    DISABLE_PROFILES_FETCH_MISSING: runtimeEnvValue('DISABLE_PROFILES_FETCH_MISSING'),
    SWEETISTICS_PROFILE_RESOLUTION_DISABLED: runtimeEnvValue('SWEETISTICS_PROFILE_RESOLUTION_DISABLED'),
    STRIPE_SECRET_KEY: runtimeEnvValue('STRIPE_SECRET_KEY'),
    RESEND_API_KEY: runtimeEnvValue('RESEND_API_KEY'),
    APIFY_API_TOKEN: runtimeEnvValue('APIFY_API_TOKEN'),
    FIRECRAWL_API_KEY: runtimeEnvValue('FIRECRAWL_API_KEY'),
    FIRECRAW_API_KEY: runtimeEnvValue('FIRECRAW_API_KEY'),
    OPENCAGE_API_KEY: runtimeEnvValue('OPENCAGE_API_KEY'),
    MAPBOX_ACCESS_TOKEN: runtimeEnvValue('MAPBOX_ACCESS_TOKEN'),
    VERCEL_KV_REST_API_URL: runtimeEnvValue('VERCEL_KV_REST_API_URL'),
    VERCEL_KV_REST_API_TOKEN: runtimeEnvValue('VERCEL_KV_REST_API_TOKEN'),
    VERCEL_KV_REST_API_READ_ONLY_TOKEN: runtimeEnvValue('VERCEL_KV_REST_API_READ_ONLY_TOKEN'),
    VAPID_PUBLIC_KEY: runtimeEnvValue('VAPID_PUBLIC_KEY'),
    VAPID_PRIVATE_KEY: runtimeEnvValue('VAPID_PRIVATE_KEY'),
    VAPID_EMAIL: runtimeEnvValue('VAPID_EMAIL'),
    ENCRYPTION_KEY: runtimeEnvValue('ENCRYPTION_KEY'),
    CRON_SECRET: runtimeEnvValue('CRON_SECRET'),
    CLI_SECRET: runtimeEnvValue('CLI_SECRET'),
    ADMIN_TWITTER_IDS: runtimeEnvValue('ADMIN_TWITTER_IDS'),
    DEBUG_SQL: runtimeEnvValue('DEBUG_SQL'),
    DEBUG_TIMELINE_CANCEL: runtimeEnvValue('DEBUG_TIMELINE_CANCEL'),
    VITEST_DEBUG_TRPC_MUTATION: runtimeEnvValue('VITEST_DEBUG_TRPC_MUTATION'),
    DEV_ENV_MODE: runtimeEnvValue('DEV_ENV_MODE'),
    DEV_ENV_LABEL: runtimeEnvValue('DEV_ENV_LABEL'),
    ENABLE_INTEGRATION_DB: runtimeEnvValue('ENABLE_INTEGRATION_DB'),
    ENABLE_TWEET_MEDIA_LINK_DEDUPE: runtimeEnvValue('ENABLE_TWEET_MEDIA_LINK_DEDUPE'),
    TWITTER_SNAPSHOTS: runtimeEnvValue('TWITTER_SNAPSHOTS'),
    ENFORCE_DB_TRANSACTIONS: runtimeEnvValue('ENFORCE_DB_TRANSACTIONS'),
    PLAYWRIGHT_TEST: runtimeEnvValue('PLAYWRIGHT_TEST'),
    PLAYWRIGHT_RUNTIME: runtimeEnvValue('PLAYWRIGHT_RUNTIME'),
    PLAYWRIGHT_TEST_RUNTIME: runtimeEnvValue('PLAYWRIGHT_TEST_RUNTIME'),
    PLAYWRIGHT_MOCK_AUTH: runtimeEnvValue('PLAYWRIGHT_MOCK_AUTH'),
    PLAYWRIGHT_AUTH_DEBUG: runtimeEnvValue('PLAYWRIGHT_AUTH_DEBUG'),
    PLAYWRIGHT_PULSE_ONLINE: runtimeEnvValue('PLAYWRIGHT_PULSE_ONLINE'),
    PLAYWRIGHT_AUTH_TIMESTAMP: runtimeEnvValue('PLAYWRIGHT_AUTH_TIMESTAMP'),
    SWEETISTICS_PGLITE_MODE: runtimeEnvValue('SWEETISTICS_PGLITE_MODE'),
    SWEETISTICS_SCHEMA_PATH: runtimeEnvValue('SWEETISTICS_SCHEMA_PATH'),
    VERCEL: runtimeEnvValue('VERCEL'),
    VERCEL_ENV: runtimeEnvValue('VERCEL_ENV'),
    VERCEL_REGION: runtimeEnvValue('VERCEL_REGION'),
    VERCEL_GIT_COMMIT_SHA: runtimeEnvValue('VERCEL_GIT_COMMIT_SHA'),
    VERCEL_DEPLOYMENT_ID: runtimeEnvValue('VERCEL_DEPLOYMENT_ID'),
    NEXT_RUNTIME: runtimeEnvValue('NEXT_RUNTIME'),
    HOSTNAME: runtimeEnvValue('HOSTNAME'),
    AWS_LAMBDA_FUNCTION_NAME: runtimeEnvValue('AWS_LAMBDA_FUNCTION_NAME'),
    NODE_ENV: runtimeEnvValue('NODE_ENV'),
    SWEETISTICS_LOG_LEVEL: runtimeEnvValue('SWEETISTICS_LOG_LEVEL'),
    LANGFUSE_SECRET_KEY: runtimeEnvValue('LANGFUSE_SECRET_KEY'),
    LANGFUSE_PUBLIC_KEY: runtimeEnvValue('LANGFUSE_PUBLIC_KEY'),
    LANGFUSE_HOST: runtimeEnvValue('LANGFUSE_HOST'),
    BETTERSTACK_API_TOKEN: runtimeEnvValue('BETTERSTACK_API_TOKEN'),
    BETTERSTACK_QUERY_ENDPOINT: runtimeEnvValue('BETTERSTACK_QUERY_ENDPOINT'),
    BETTERSTACK_QUERY_USERNAME: runtimeEnvValue('BETTERSTACK_QUERY_USERNAME'),
    BETTERSTACK_QUERY_PASSWORD: runtimeEnvValue('BETTERSTACK_QUERY_PASSWORD'),
    TWITTER_GRAPHQL_AUTH_TOKEN: runtimeEnvValue('TWITTER_GRAPHQL_AUTH_TOKEN'),
    TWITTER_GRAPHQL_CT0: runtimeEnvValue('TWITTER_GRAPHQL_CT0'),
    AUTH_TOKEN: runtimeEnvValue('AUTH_TOKEN'),
    CT0: runtimeEnvValue('CT0'),
    BETTERSTACK_DEFAULT_SOURCE: runtimeEnvValue('BETTERSTACK_DEFAULT_SOURCE'),
    EXTERNAL_API_TRACKER_STORE_RESPONSES: runtimeEnvValue('EXTERNAL_API_TRACKER_STORE_RESPONSES'),
    EXPO_PUBLIC_APP_SCHEME: runtimeEnvValue('EXPO_PUBLIC_APP_SCHEME'),
    EXPO_PUBLIC_API_URL: runtimeEnvValue('EXPO_PUBLIC_API_URL'),
    EXPO_PUBLIC_WEB_URL: runtimeEnvValue('EXPO_PUBLIC_WEB_URL'),
    EXPO_PUBLIC_AUTH_CALLBACK_PATH: runtimeEnvValue('EXPO_PUBLIC_AUTH_CALLBACK_PATH'),
    SENTRY_DSN: runtimeEnvValue('SENTRY_DSN'),

    // Client
    NEXT_PUBLIC_APP_URL: runtimeEnvValue('NEXT_PUBLIC_APP_URL'),
    NEXT_PUBLIC_BUILD_MODE: runtimeEnvValue('NEXT_PUBLIC_BUILD_MODE'),
    NEXT_PUBLIC_POSTHOG_KEY: runtimeEnvValue('NEXT_PUBLIC_POSTHOG_KEY'),
    NEXT_PUBLIC_POSTHOG_HOST: runtimeEnvValue('NEXT_PUBLIC_POSTHOG_HOST'),
    NEXT_PUBLIC_VAPID_PUBLIC_KEY: runtimeEnvValue('NEXT_PUBLIC_VAPID_PUBLIC_KEY'),
    NEXT_PUBLIC_MSW: runtimeEnvValue('NEXT_PUBLIC_MSW'),
    NEXT_PUBLIC_API_MOCK: runtimeEnvValue('NEXT_PUBLIC_API_MOCK'),
    NEXT_PUBLIC_TWITTER_SNAPSHOTS: runtimeEnvValue('NEXT_PUBLIC_TWITTER_SNAPSHOTS'),
    NEXT_PUBLIC_PLAYWRIGHT_TEST: runtimeEnvValue('NEXT_PUBLIC_PLAYWRIGHT_TEST'),
    NEXT_PUBLIC_ENABLE_RT_DEBUG: runtimeEnvValue('NEXT_PUBLIC_ENABLE_RT_DEBUG'),
    NEXT_PUBLIC_TRPC_BATCH_DEV: runtimeEnvValue('NEXT_PUBLIC_TRPC_BATCH_DEV'),
    NEXT_PUBLIC_TWITTER_DATA_SOURCE: runtimeEnvValue('NEXT_PUBLIC_TWITTER_DATA_SOURCE'),
    NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN: runtimeEnvValue('NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN'),
    NEXT_PUBLIC_BETTER_STACK_INGESTING_URL: runtimeEnvValue('NEXT_PUBLIC_BETTER_STACK_INGESTING_URL'),
    NEXT_PUBLIC_VERCEL_ENV: runtimeEnvValue('NEXT_PUBLIC_VERCEL_ENV'),
    NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN: runtimeEnvValue('NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN'),
    NEXT_PUBLIC_TWEET_MEDIA_DEBUG: runtimeEnvValue('NEXT_PUBLIC_TWEET_MEDIA_DEBUG'),
  },

  /**
   * Makes sure you don't accidentally access client-side variables on the server.
   * Also prevents server variables from being accessed in client components.
   * @see https://env.t3.gg/docs/nextjs#server-crashes-on-client-side-access
   */
  skipValidation: (() => {
    if (globalThis.window !== undefined) {
      // Client bundles don't receive the full env bag; skip validation in the browser.
      return true;
    }
    if (process === undefined) {
      // Client bundles run without a Node `process` shim; skip validation to avoid runtime crashes.
      return true;
    }
    type SkipValidationEnv = NodeJS.ProcessEnv & { SKIP_ENV_VALIDATION?: string };
    const skipFlags = process.env as SkipValidationEnv;
    const nodeEnv = (process.env.NODE_ENV ?? '').toLowerCase();
    const serverSkipFlag = normalizeBooleanFlag(skipFlags.SKIP_ENV_VALIDATION ?? 'false') === 'true';
    const clientSkipFlag = normalizeBooleanFlag(skipFlags.SKIP_ENV_VALIDATION ?? 'false') === 'true';
    const vercelPreview = runtimeEnvValue('VERCEL') === '1' && runtimeEnvValue('VERCEL_ENV') !== 'production';
    return serverSkipFlag || clientSkipFlag || vercelPreview || nodeEnv === 'test';
  })(),

  /**
   * Converts empty strings to `undefined` before validation.
   * This allows Zod's optional() to work correctly with empty env vars.
   * Without this, `MY_VAR=""` would fail validation instead of being treated as undefined.
   * @see https://env.t3.gg/docs/nextjs#empty-strings
   */
  emptyStringAsUndefined: true,

  /**
   * Custom error formatting for better developer experience.
   * Provides clear, actionable error messages when environment validation fails.
   */
  onValidationError: (issues) => {
    console.error('❌ Environment variable validation failed:');
    console.error('Issues found:');
    for (const issue of issues) {
      const pathLabel = Array.isArray(issue.path) ? issue.path.map(String).join('.') : 'root';
      console.error(`- ${pathLabel || 'root'}: ${issue.message}`);
    }
    console.error('');
    console.error('Tips:');
    console.error('- Check your .env file for missing or invalid variables');
    console.error('- Run `pnpm env:status` to see your current environment');
    console.error('- Use `pnpm env:dev` or `pnpm env:prod` to switch environments');
    console.error('- Set SKIP_ENV_VALIDATION=true to temporarily bypass validation');
    throw new Error('Environment variable validation failed');
  },

  /**
   * Callback when environment variables are successfully validated.
   * Provides helpful development information without being verbose in production.
   */
  onInvalidAccess: (variable) => {
    // Use a safer check that doesn't rely on NODE_ENV
    const browserWindow = getBrowserWindow();
    const isProduction = browserWindow ? browserWindow.location.hostname !== 'localhost' : false;

    if (!isProduction) {
      console.warn(`⚠️ Attempted to access server variable "${variable}" on the client.`);
      console.warn('This will cause a runtime error in production.');
    }
    throw new Error(
      isProduction
        ? `❌ Attempted to access server environment variable on client: ${variable}`
        : `❌ You tried to access the server-side environment variable "${variable}" on the client, but it doesn't exist. ` +
            'Check if you meant to use a NEXT_PUBLIC_ or EXPO_PUBLIC_ prefixed variable instead.'
    );
  },
});

type EnvironmentInstance = typeof environmentInstance;

const BOOLEAN_TRUE_VALUES = new Set(['1', 'true', 'yes', 'on', 'enabled']);
const BOOLEAN_FALSE_VALUES = new Set(['0', 'false', 'no', 'off', 'disabled']);

const BOOLEAN_ENV_KEYS = BOOLEAN_ENV_KEY_NAMES;

type BooleanEnvironmentKey = (typeof BOOLEAN_ENV_KEYS)[number];
type AssertTrue<T extends true> = T;
type _BooleanEnvKeysAreValid = AssertTrue<BooleanEnvironmentKey extends keyof EnvironmentInstance ? true : false>;
type _BooleanEnvKeysNotAll = AssertTrue<keyof EnvironmentInstance extends BooleanEnvironmentKey ? false : true>;
type _AppUrlNotBooleanKey = AssertTrue<'NEXT_PUBLIC_APP_URL' extends BooleanEnvironmentKey ? false : true>;
type _AppUrlIsString = AssertTrue<EnvironmentCoerced['NEXT_PUBLIC_APP_URL'] extends string | undefined ? true : false>;
type EnvironmentCoerced = Omit<EnvironmentInstance, BooleanEnvironmentKey> & { [K in BooleanEnvironmentKey]: boolean };

const booleanKeySet = BOOLEAN_ENV_KEY_SET;

const coerceBoolean = (candidate: unknown): boolean => {
  if (typeof candidate === 'boolean') {
    return candidate;
  }
  if (typeof candidate === 'string') {
    const normalized = candidate.trim().toLowerCase();
    if (normalized.length === 0) {
      return false;
    }
    if (BOOLEAN_TRUE_VALUES.has(normalized)) {
      return true;
    }
    if (BOOLEAN_FALSE_VALUES.has(normalized)) {
      return false;
    }
    return true;
  }
  if (typeof candidate === 'number') {
    return candidate !== 0;
  }
  return Boolean(candidate);
};

const isEnvironmentCoerced = (candidate: unknown): candidate is EnvironmentCoerced => {
  if (!isRecord(candidate)) {
    return false;
  }
  return BOOLEAN_ENV_KEYS.every((key) => typeof candidate[key] === 'boolean');
};

const isBooleanEnvironmentKey = (key: keyof EnvironmentInstance): key is BooleanEnvironmentKey =>
  typeof key === 'string' && booleanKeySet.has(key);

const coerceEnvironment = (source: EnvironmentInstance): EnvironmentCoerced => {
  type PartialEnvironment = Partial<Record<keyof EnvironmentCoerced, EnvironmentCoerced[keyof EnvironmentCoerced]>>;
  const isEnvironmentKey = (key: string): key is keyof EnvironmentInstance => key in source;
  const draft: PartialEnvironment = {};

  for (const [key, currentValue] of Object.entries(source)) {
    if (!isEnvironmentKey(key)) {
      continue;
    }

    if (isBooleanEnvironmentKey(key)) {
      draft[key] = coerceBoolean(currentValue);
      continue;
    }

    if (typeof currentValue === 'string') {
      const trimmed = currentValue.trim();
      draft[key] = trimmed.length > 0 ? trimmed : undefined;
      continue;
    }

    draft[key] = currentValue;
  }

  for (const key of BOOLEAN_ENV_KEYS) {
    if (draft[key] === undefined) {
      draft[key] = false;
    }
  }

  if (!isEnvironmentCoerced(draft)) {
    throw new Error('Boolean environment keys failed to coerce');
  }

  return draft;
};

/**
 * React Native environments don't expose process.env, so we throw on access to avoid silent failures.
 * Other runtimes (Next.js server/client) use the validated env instance.
 */
const reactNativeEnvironmentStub: EnvironmentCoerced = new Proxy(coerceEnvironment(environmentInstance), {
  get: (_target, property) => {
    throw new Error(
      `Environment variables are not available in the React Native runtime (attempted to access "${String(property)}").`
    );
  },
});

const envInstance = isReactNativeRuntime ? reactNativeEnvironmentStub : coerceEnvironment(environmentInstance);
export const env: Env = Object.freeze(envInstance);
export type Env = EnvironmentCoerced;
